import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import {
  type CompanyDetailDto,
  CompanyService,
  type CompanyUpdateRequest,
  type InterviewBasicDtoPagedResponseDto,
  type UserBasicDto,
  type UserDepartmentRequest,
  type UserPositionRequest,
  type UserPutRequest,
  type UserRequest,
  type UserRoleRequest,
} from '@/services/api';

const accountSettingsKeys = {
  getCompany: () => ['getCompany'] as const,
  getCompanyUsers: () => ['getCompanyUsers'] as const,
  getCompanyUserCreatedInterviews: (
    userId: string,
    params?: {
      q?: string;
      page?: number;
      size?: number;
      startDateFrom?: string;
      startDateTo?: string;
      isTemplateInterview?: boolean;
    }
  ) => ['getCompanyUserCreatedInterviews', userId, params] as const,
  //   getEmailTemplate: (emailTemplateId: string) =>
  //     ['getEmailTemplate', emailTemplateId] as const,
};

export const useGetCompany = (
  options?: Partial<UseQueryOptions<CompanyDetailDto, AxiosError>>
) => {
  return useQuery<CompanyDetailDto, AxiosError>({
    queryKey: accountSettingsKeys.getCompany(),
    queryFn: () => CompanyService.getCompanyInfo(),
    ...options,
  });
};

export const useGetCompanyUsers = (
  options?: Partial<UseQueryOptions<Array<UserBasicDto>, AxiosError>>
) => {
  return useQuery<Array<UserBasicDto>, AxiosError>({
    queryKey: accountSettingsKeys.getCompanyUsers(),
    queryFn: () => CompanyService.getCompanyUser(),
    ...options,
  });
};

export const useDeleteCompanyUserMutation = (
  options?: Partial<UseMutationOptions<void, AxiosError, { userId: string }>>
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { userId: string }>({
    mutationFn: ({ userId }) =>
      CompanyService.deleteCompanyUserById({ id: userId }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: accountSettingsKeys.getCompanyUsers(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useCompanyUserAddMutation = (
  options?: UseMutationOptions<string, AxiosError, UserRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError, UserRequest>({
    mutationFn: async (body) => {
      return CompanyService.postCompanyUser({ body });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: accountSettingsKeys.getCompanyUsers(),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useEditCompanyUserMutation = (
  options?: UseMutationOptions<string, AxiosError, UserPutRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError, UserPutRequest>({
    mutationFn: async (body) => {
      return CompanyService.putCompanyUser({ body });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: accountSettingsKeys.getCompanyUsers(),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useResendUserInvitationMutation = (
  options?: UseMutationOptions<string, AxiosError, { userId: string }>
) => {
  const queryClient = useQueryClient();
  return useMutation<string, AxiosError, { userId: string }>({
    mutationFn: async ({ userId }) => {
      return CompanyService.postAllCompanyUserResend({ userId });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: accountSettingsKeys.getCompanyUsers(),
        exact: false,
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useEditCompanyUserDepartmentMutation = (
  options?: UseMutationOptions<string, AxiosError, UserDepartmentRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError, UserDepartmentRequest>({
    mutationFn: async (body) => {
      return CompanyService.putCompanyUserDepartment({ body });
    },
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: accountSettingsKeys.getCompanyUsers(),
      });

      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useEditCompanyUserRoleMutation = (
  options?: UseMutationOptions<string, AxiosError, UserRoleRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError, UserRoleRequest>({
    mutationFn: async (body) => {
      return CompanyService.putCompanyUserRole({ body });
    },
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: accountSettingsKeys.getCompanyUsers(),
      });

      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useEditCompanyUserPositionMutation = (
  options?: UseMutationOptions<string, AxiosError, UserPositionRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError, UserPositionRequest>({
    mutationFn: async (body) => {
      return CompanyService.putCompanyUserPosition({ body });
    },
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: accountSettingsKeys.getCompanyUsers(),
      });

      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useGetCompanyUserCreatedInterviews = (
  userId: string,
  params?: {
    q?: string;
    page?: number;
    size?: number;
    startDateFrom?: string;
    startDateTo?: string;
    isTemplateInterview?: boolean;
  },
  options?: Partial<
    UseQueryOptions<InterviewBasicDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<InterviewBasicDtoPagedResponseDto, AxiosError>({
    queryKey: accountSettingsKeys.getCompanyUserCreatedInterviews(
      userId,
      params
    ),
    queryFn: () =>
      CompanyService.getAllCompanyUserCreatedInterviews({ userId, ...params }),
    enabled: !!userId,
    ...options,
  });
};

export const useUpdateCompanyMutation = (
  options?: UseMutationOptions<string, AxiosError, CompanyUpdateRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError, CompanyUpdateRequest>({
    mutationFn: async (body) => {
      return CompanyService.putCompany({ body });
    },
    ...options,
    async onSuccess(data, variables, context) {
      // Invalidate relevant queries after successful update
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: accountSettingsKeys.getCompany(),
        }),
        queryClient.invalidateQueries({
          queryKey: accountSettingsKeys.getCompanyUsers(),
        }),
      ]);

      options?.onSuccess?.(data, variables, context);
    },
  });
};
